import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

interface Profile {
  id: string;
  user_id: string;
  username: string;
  pokemon_id: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
  is_pokemon_id_public: boolean;
  is_collection_public: boolean;
  pokemon_id_description: string | null;
}

interface ProfileUpdate {
  username?: string;
  pokemon_id?: string | null;
  is_pokemon_id_public?: boolean;
  is_collection_public?: boolean;
  pokemon_id_description?: string | null;
  avatar_url?: string | null;
}

export function useProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      try {
        const { data, error } = await supabase
          .rpc('get_or_create_profile', { user_id: user.id });

        if (error) throw error;
        setProfile(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError(err instanceof Error ? err.message : 'Erreur lors du chargement du profil');
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user]);

  const validateUsername = (username: string) => {
    if (username.length < 3) {
      throw new Error('Le nom d\'utilisateur doit contenir au moins 3 caractères');
    }
    if (!/^[a-z0-9][a-z0-9_-]{1,28}[a-z0-9]$/.test(username)) {
      throw new Error('Format de nom d\'utilisateur invalide');
    }
  };

  const validatePokemonId = (pokemonId: string | null) => {
    if (pokemonId && !/^\d{4}-\d{4}-\d{4}-\d{4}$/.test(pokemonId)) {
      throw new Error('Format d\'ID-ami invalide (XXXX-XXXX-XXXX-XXXX)');
    }
  };

  const updateProfile = async (updates: ProfileUpdate) => {
    if (!user) throw new Error('Utilisateur non authentifié');

    try {
      if (updates.username) {
        validateUsername(updates.username);
      }
      if (updates.pokemon_id !== undefined) {
        validatePokemonId(updates.pokemon_id);
      }

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('user_id', user.id);

      if (error) {
        if (error.code === '23505') {
          throw new Error('Ce nom d\'utilisateur est déjà pris');
        }
        throw error;
      }

      setProfile(prev => prev ? { ...prev, ...updates } : null);
      setError(null);
    } catch (err) {
      console.error('Error updating profile:', err);
      throw err;
    }
  };

  return {
    profile,
    loading,
    error,
    updateProfile
  };
}